import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Search, ChevronLeft, ChevronRight, Newspaper, ExternalLink } from "lucide-react";

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function stripHtml(html) {
  return DOMPurify.sanitize(html || "", { ALLOWED_TAGS: [] });
}

export default function Actualites() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState(params.get("search") || "");
  const [error, setError] = useState(null);

  const page = parseInt(params.get("page") || "1", 10);
  const search = params.get("search") || "";
  const categorie = params.get("categorie") || "";

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page: String(page) });
    if (search) qs.set("search", search);
    if (categorie) qs.set("categorie", categorie);
    setError(null);
    api(`/actualites?${qs}`).then(setData).catch((e) => setError(e.message));
  }, [page, search, categorie]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api("/actualites/categories").then(setCategories).catch(() => {});
  }, []);

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  return (
    <AppLayout>
      <div data-testid="news-page" className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <Newspaper className="h-7 w-7 text-cfhtb-primary" />
          <div>
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Actualités</h1>
            <p className="text-sm text-cfhtb-text">En direct du site cfhtb.org</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4 space-y-4">
          <form onSubmit={(e) => { e.preventDefault(); updateParams({ search: searchInput }); }} className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="news-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Rechercher un article…"
              className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light"
            />
          </form>
          <div className="flex flex-wrap gap-2" data-testid="news-categories">
            <button
              data-testid="news-category-all"
              onClick={() => updateParams({ categorie: "" })}
              className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                !categorie ? "bg-cfhtb-primary text-white border-cfhtb-primary" : "bg-white text-cfhtb-text border-slate-300 hover:border-cfhtb-light"
              }`}
            >
              Toutes
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`news-category-${c.slug}`}
                onClick={() => updateParams({ categorie: String(c.id) })}
                className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                  categorie === String(c.id) ? "bg-cfhtb-primary text-white border-cfhtb-primary" : "bg-white text-cfhtb-text border-slate-300 hover:border-cfhtb-light"
                }`}
              >
                {c.nom} ({c.nb})
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <div className="bg-white rounded-lg border border-cfhtb-warning/40 p-6 text-sm text-cfhtb-text" data-testid="news-error">{error}</div>
        ) : !data ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : data.items.length === 0 && (data.locales || []).length === 0 ? (
          <p className="text-sm text-cfhtb-text" data-testid="news-empty">Aucun article ne correspond à votre recherche.</p>
        ) : (
          <>
            {(data.locales || []).length > 0 && (
              <section data-testid="news-local-section">
                <h2 className="text-xs uppercase tracking-wider font-semibold text-cfhtb-primary mb-3">
                  Annonces de la CFHTB
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {data.locales.map((a) => (
                    <Link
                      key={a.id}
                      to={`/actualites/${a.slug}`}
                      data-testid={`news-local-card-${a.slug}`}
                      className="bg-white rounded-lg border-2 border-cfhtb-primary/20 shadow-sm overflow-hidden hover:shadow-md hover:border-cfhtb-primary/40 transition-all group flex flex-col"
                    >
                      {a.image ? (
                        <div className="h-40 w-full bg-white flex items-center justify-center overflow-hidden border-b border-slate-100">
                          <img src={a.image} alt="" className="max-h-full max-w-full w-auto h-auto object-contain p-2" loading="lazy" />
                        </div>
                      ) : (
                        <div className="h-40 bg-gradient-to-br from-cfhtb-primary to-cfhtb-secondary flex items-center justify-center">
                          <Newspaper className="h-8 w-8 text-white/60" />
                        </div>
                      )}
                      <div className="p-5 flex-1 flex flex-col">
                        <p className="text-xs text-cfhtb-secondary font-medium">{formatDate(a.date)}</p>
                        <h2 className="font-serif text-lg font-semibold text-cfhtb-heading mt-1.5 leading-snug group-hover:text-cfhtb-primary transition-colors">
                          {a.titre}
                        </h2>
                        <p className="text-sm text-cfhtb-text mt-2 line-clamp-3">{a.extrait}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-grid">
              {data.items.map((a) => (
                <Link
                  key={a.id}
                  to={`/actualites/${a.slug}`}
                  data-testid={`news-card-${a.slug}`}
                  className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden hover:shadow-md hover:border-cfhtb-light transition-all group flex flex-col"
                >
                  {a.image ? (
                    <div className="h-40 w-full bg-white flex items-center justify-center overflow-hidden border-b border-slate-100">
                      <img src={a.image} alt="" className="max-h-full max-w-full w-auto h-auto object-contain p-2" loading="lazy" />
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-cfhtb-bg to-cfhtb-light/30 flex items-center justify-center">
                      <Newspaper className="h-8 w-8 text-cfhtb-secondary/50" />
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-xs text-cfhtb-secondary font-medium">{formatDate(a.date)}</p>
                    <h2
                      className="font-serif text-lg font-semibold text-cfhtb-heading mt-1.5 leading-snug group-hover:text-cfhtb-primary transition-colors"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.titre) }}
                    />
                    <p className="text-sm text-cfhtb-text mt-2 line-clamp-3">{stripHtml(a.extrait)}</p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="flex items-center justify-center gap-3 pt-2">
              <button data-testid="news-prev" disabled={page <= 1} onClick={() => updateParams({ page: String(page - 1) })}
                className="inline-flex items-center gap-1 text-sm font-medium text-cfhtb-primary border border-cfhtb-primary rounded-md px-3 py-1.5 hover:bg-cfhtb-bg disabled:opacity-40 transition-colors">
                <ChevronLeft className="h-4 w-4" /> Précédent
              </button>
              <span className="text-sm text-cfhtb-text" data-testid="news-pagination-info">Page {data.page} / {data.total_pages}</span>
              <button data-testid="news-next" disabled={page >= data.total_pages} onClick={() => updateParams({ page: String(page + 1) })}
                className="inline-flex items-center gap-1 text-sm font-medium text-cfhtb-primary border border-cfhtb-primary rounded-md px-3 py-1.5 hover:bg-cfhtb-bg disabled:opacity-40 transition-colors">
                Suivant <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
